import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * List — a styled collection of items for menus, settings panels, and item
 * groups. Design adopted from the TailGrids list model (the system our Figma is
 * built on), wired to First Bite tokens. Render plain `<li>` children; the List
 * styles them via descendant selectors so markup stays semantic.
 *
 *   direction     vertical | horizontal
 *   hideDividers  drop the hairlines between items
 *
 * Per-item hooks (set on the `<li>` / inner `<span>`):
 *   data-active            highlights the current item (basil surface + text)
 *   data-type="count"      a trailing count badge, auto-pushed right (ml-auto)
 *
 * Leading `<svg>` icons are auto-sized to 16px and inherit the item's text
 * color. Vertical lists cap at 228px wide; horizontal lists shrink to content.
 */
const listVariants = cva(
  cn(
    'flex overflow-hidden rounded-lg border border-fbui-gray-200',
    // Per-item base + states (items are plain <li> children).
    '[&>li]:flex [&>li]:items-center [&>li]:tracking-[-0.2px] [&>li]:text-fbui-gray-700 [&>li]:transition-colors',
    '[&>li:hover]:bg-fbui-gray-50',
    '[&>li[data-active]]:bg-fbui-basil-50 [&>li[data-active]]:font-medium [&>li[data-active]]:text-fbui-basil-700',
    // Trailing count badge + leading icons.
    '[&_[data-type=count]]:ml-auto [&_[data-type=count]]:text-fbui-gray-500',
    '[&_svg]:shrink-0'
  ),
  {
    variants: {
      direction: {
        vertical: 'flex-col',
        horizontal: 'w-fit flex-row items-center',
      },
      size: {
        sm: 'text-xs [&>li]:gap-2 [&>li]:px-3 [&>li]:py-1.5 [&_svg]:size-3.5',
        md: 'text-sm [&>li]:gap-2.5 [&>li]:px-4 [&>li]:py-2.5 [&_svg]:size-4',
      },
      hideDividers: { true: '', false: '' },
    },
    compoundVariants: [
      // Vertical lists cap their width (228px md / 200px sm).
      { direction: 'vertical', size: 'md', className: 'max-w-[228px]' },
      { direction: 'vertical', size: 'sm', className: 'max-w-[200px]' },
      {
        direction: 'vertical',
        hideDividers: false,
        className: 'divide-y divide-fbui-gray-200',
      },
      {
        direction: 'horizontal',
        hideDividers: false,
        className: 'divide-x divide-fbui-gray-200',
      },
    ],
    defaultVariants: { direction: 'vertical', size: 'md', hideDividers: false },
  }
)

export interface ListProps
  extends
    React.HTMLAttributes<HTMLUListElement>,
    Omit<VariantProps<typeof listVariants>, 'hideDividers'> {
  /** Hide the hairlines between items. */
  hideDividers?: boolean
}

export const List = React.forwardRef<HTMLUListElement, ListProps>(
  ({ className, direction, size, hideDividers = false, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn(listVariants({ direction, size, hideDividers, className }))}
      {...props}
    />
  )
)
List.displayName = 'List'
